// Birim düzenleyicisi — sistem kütüphanesindeki birim sözlüğü.
//
// Kullanıcı isteği: "kullanıcı birim ekleme çıkarma yapabilmeli ve bu
// birimler kullanılmalı tüm sistemde".
//
// Birim, tablolar arasında yabancı anahtarla DEĞİL kod metniyle bağlı. Bu iki
// davranışı zorunlu kılıyor ve ikisi de sunucuda uygulanıyor:
//   · Kod değişirse kütüphanedeki geçişler birlikte taşınır.
//   · Kullanımdaki kod silinemez (SY5) — 409 döner, kapatmak önerilir.
// Buradaki uyarı metinleri o kuralları önceden söylüyor; kullanıcı 409'u
// görmek için kaydetmeyi denemek zorunda kalmasın.

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde, useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { sistem, type SistemBirim, type StokTuru } from './api'

const TURLER: { deger: '' | StokTuru; etiket: string }[] = [
  { deger: '', etiket: 'Tüm türler' },
  { deger: 'hammadde', etiket: 'Yalnız hammadde' },
  { deger: 'urun', etiket: 'Yalnız ürün' },
  { deger: 'sarf', etiket: 'Yalnız sarf' },
  { deger: 'yedek', etiket: 'Yalnız yedek' },
  { deger: 'yakit', etiket: 'Yalnız yakıt' },
]

export default function BirimDrawer({
  mevcut,
  sonrakiSira = 0,
}: {
  mevcut?: SistemBirim
  /** Yeni kaydın varsayılan sırası — listenin SONU. */
  sonrakiSira?: number
}) {
  const kuyruk = useQueryClient()
  const { kapat } = useDrawer()
  const duzenleme = !!mevcut

  const [kod, setKod] = useState(mevcut?.kod ?? '')
  const [ad, setAd] = useState(mevcut?.ad ?? '')
  const [tur, setTur] = useState<'' | StokTuru>(mevcut?.tur ?? '')
  // Yeni birim listenin sonuna: 0 verilseydi her yeni birim tüm açılır
  // listelerin tepesine çıkardı.
  const [sira, setSira] = useState(String(mevcut?.sira ?? sonrakiSira))
  const [aktif, setAktif] = useState(mevcut?.aktif ?? true)

  const kodDegisti = duzenleme && kod.trim() !== mevcut!.kod

  const engeller: string[] = []
  if (!kod.trim()) engeller.push('Kod girin')
  if (kod.trim().length > 16) engeller.push('Kod en çok 16 karakter')
  if (ad.trim().length < 2) engeller.push('Birim adı girin')
  const gecerli = engeller.length === 0

  const hataMetni = (h: unknown) =>
    h instanceof ApiHatasi
      ? h.detay?.length
        ? `${h.message} — ${h.detay.join(' · ')}`
        : h.message
      : 'İşlem tamamlanamadı'

  const tazele = () => kuyruk.invalidateQueries({ queryKey: ['sistem-kut-birim'] })

  const kaydet = useMutation({
    mutationFn: () =>
      sistem.birimYaz(mevcut?.id ?? null, {
        kod: kod.trim(),
        ad: ad.trim(),
        tur: tur === '' ? null : tur,
        sira: Number(sira) || 0,
        aktif,
      }),
    onSuccess: (x) => {
      void tazele()
      toast(
        duzenleme
          ? `${x.kod} kaydedildi${kodDegisti ? ' · kütüphanedeki geçişler taşındı' : ''}`
          : `${x.kod} eklendi · birim listelerine düştü`,
      )
      kapat()
    },
    onError: (h) => toast(hataMetni(h)),
  })

  const sil = useMutation({
    mutationFn: () => sistem.birimSil(mevcut!.id),
    onSuccess: () => {
      void tazele()
      toast(`${mevcut!.kod} kütüphaneden kaldırıldı`)
      kapat()
    },
    // 409 SY5: kullanımdaysa sunucu engelliyor, detayda nerede geçtiği yazıyor.
    onError: (h) => toast(hataMetni(h)),
  })

  const bekliyor = kaydet.isPending || sil.isPending

  return (
    <>
      <DrawerGovde>
        <div className="fgrid">
          <div className="fld">
            <label>Kod</label>
            <input
              className="in mono"
              value={kod}
              placeholder="t"
              autoComplete="off"
              maxLength={16}
              onChange={(e) => setKod(e.target.value)}
            />
          </div>
          <div className="fld">
            <label>Ad</label>
            <input
              className="in"
              value={ad}
              placeholder="ton"
              autoComplete="off"
              maxLength={64}
              onChange={(e) => setAd(e.target.value)}
            />
          </div>
        </div>

        {kodDegisti && (
          <div className="rule">
            <b>Kod değişiyor</b>
            <span>
              <b>{mevcut!.kod}</b> → <b>{kod.trim()}</b>. Kütüphanede bu birimi
              kullanan kayıtlar birlikte güncellenir; tesislerdeki eski değerler
              olduğu gibi kalır ve seçim listelerinde görünmeye devam eder.
            </span>
          </div>
        )}

        <div className="fgrid">
          <div className="fld">
            <label>Tür kısıtı</label>
            <select
              className="in"
              value={tur}
              onChange={(e) => setTur(e.target.value as '' | StokTuru)}
            >
              {TURLER.map((t) => (
                <option key={t.deger} value={t.deger}>
                  {t.etiket}
                </option>
              ))}
            </select>
            <div className="fld-hint">Seçilirse birim yalnız o türün formunda önerilir.</div>
          </div>
          <div className="fld">
            <label>Sıra</label>
            <input
              className="in mono"
              value={sira}
              inputMode="numeric"
              autoComplete="off"
              onChange={(e) => setSira(e.target.value)}
            />
          </div>
        </div>

        <button
          className="swrow"
          type="button"
          role="switch"
          aria-checked={aktif}
          onClick={() => setAktif((v) => !v)}
        >
          <span>Birim açık</span>
          <small>
            {aktif ? 'seçim listelerinde görünür' : 'kapalıysa yeni kayıtlarda önerilmez'}
          </small>
          <span className={aktif ? 'sw on' : 'sw'} />
        </button>

        <div className="rule">
          <b>Not</b>
          <span>
            Birim sözlüğü <b>tüm sistemde ortak</b>: buradaki değişiklik stok
            kartı formundan manuel harekete kadar her ekrana anında yansır —
            diğer kütüphanelerin aksine tesise kopyalanmaz.
          </span>
        </div>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-ghost" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        {duzenleme && (
          <button
            className="btn btn-dan"
            type="button"
            disabled={bekliyor}
            onClick={() => sil.mutate()}
          >
            Sil
          </button>
        )}
        <button
          className="btn btn-pri"
          type="button"
          style={{ flex: 1, justifyContent: 'center' }}
          disabled={!gecerli || bekliyor}
          title={gecerli ? undefined : `Eksik: ${engeller.join(' · ')}`}
          onClick={() => kaydet.mutate()}
        >
          {duzenleme ? 'Kaydet' : 'Birimi ekle'}
        </button>
      </DrawerAltlik>
    </>
  )
}
